import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  of,
  switchMap,
} from 'rxjs';
import { NavigationService } from '../../../core/services/utils/navigation.service';
import { SuggestionsService } from '../../../core/services/utils';
import {
  MatAutocomplete,
  MatAutocompleteModule,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { sortBySortiment } from '../../utils';
import { deserializeResponse } from '../../../core/services/utils/helpers';
import { Product } from '../../models/product.model';
import { Suggestions } from '../../models/suggestions.model';

@Component({
  selector: 'app-item-search',
  templateUrl: './item-search.component.html',
  styleUrls: ['./item-search.component.scss'],
  imports: [
    MatFormFieldModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    CommonModule,
    MatAutocompleteModule,
    ScrollingModule,
    CommonModule,
  ],
})
export class ItemSearchComponent implements OnInit {
  searchControl = new FormControl('');
  options: any = [];
  panelWidth: string = '';
  previousValue: string = '';
  isFocused: boolean = false;
  @Input() placeholder: string = 'Schlagwort / Produkt / Produkt-Nr.';
  @Input() onlyIds: boolean = false;
  @Input() idTags: { id: number; valid: boolean }[] = [];
  @Output() productSelected = new EventEmitter<number>();
  @Output() productRemoved = new EventEmitter<number>();

  @ViewChild('searchContainer', { static: true }) searchContainer!: ElementRef;
  @ViewChild(MatAutocompleteTrigger)
  autocompleteTrigger: MatAutocompleteTrigger;
  @ViewChild('searchInput') searchInput: ElementRef;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private navigationService: NavigationService,
    private suggestionsService: SuggestionsService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        distinctUntilChanged((prev, curr) => prev.search === curr.search),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ search }) => {
        if (!this.onlyIds) this.searchControl.setValue(search || '');
      });

    this.searchControl.valueChanges
      .pipe(
        debounceTime(500),
        takeUntilDestroyed(this.destroyRef),
        filter(value => {
          if (this.onlyIds) {
            const numeric = value.replace(/\D+/g, ''); // remove non-digits
            if (numeric !== value) {
              this.searchControl.setValue(numeric, { emitEvent: false });
            }
            return numeric.length >= 4;
          }
          return value.length >= 3;
        }),
        switchMap(value =>
          this.suggestionsService.getSuggestions<Suggestions>({
            query: value,
            clazz: Suggestions,
          })
        )
      )
      .subscribe(suggestions => {
        if (this.onlyIds) {
          this.options = suggestions.data.products
            .sort((a, b) => sortBySortiment(a, b))
            .map(product => ({
              ...product,
              type: 'product',
            }));
        } else {
          this.options = [
            {
              name: 'Kategorien',
              suggestions: [
                ...suggestions.data.categories.map(category => ({
                  ...category,
                  type: 'category',
                })),
                ...suggestions.data.subcategories.map(subcategory => ({
                  ...subcategory,
                  type: 'subcategory',
                })),
              ],
            },
            {
              name: 'Produkte',
              suggestions: suggestions.data.products
                .sort((a, b) => sortBySortiment(a, b))
                .map(product => ({
                  ...product,
                  type: 'product',
                })),
            },
          ];
        }
      });
  }

  getPanelWidth(): string {
    return this.searchContainer.nativeElement.offsetWidth + 'px';
  }

  onKeyUp(event: KeyboardEvent): void {
    if (this.onlyIds && this.searchControl.value.length < 4) return;
    const triggerKeys = this.onlyIds ? ['Enter', ' ', ','] : ['Enter'];

    if (triggerKeys.includes(event.key)) {
      event.preventDefault();
      this.onSearch();
    }
  }

  onSearch(): void {
    if (this.onlyIds) {
      this.productSelected.emit(+this.searchControl.value);
      this.searchControl.setValue('');
      this.previousValue = '';
      this.searchInput.nativeElement.blur();
      this.searchInput.nativeElement.focus();
      this.autocompleteTrigger.closePanel();
      this.options = [];

      return;
    }
    if (
      Number.isFinite(Number(this.searchControl.value)) &&
      this.options[1]?.suggestions.length == 1
    ) {
      this.onSuggestionClick(this.options[1].suggestions[0]);
      return;
    }

    if (this.router.url.includes('/shop/category')) {
      this.navigationService.setPreviousUrl(this.router.url);
    }
    if (this.searchControl.value.trim()) {
      this.router.navigate(['/shop/search'], {
        queryParams: {
          search: this.searchControl.value,
          categoryId: null,
          subcategory: null,
          brands: null,
          alternativeFor: null,
          productId: null,
          sort: null,
          minPrice: null,
          maxPrice: null,
          adBannerId: null,
        },
        queryParamsHandling: 'merge',
      });
      this.searchControl.setValue('');
      this.autocompleteTrigger.closePanel();
    }
  }

  onSuggestionClick(suggestion: any): void {
    this.searchControl.setValue('');
    this.previousValue = '';
    this.searchInput.nativeElement.blur();
    this.autocompleteTrigger.closePanel();
    this.options = [];
    if (this.onlyIds) {
      this.productSelected.emit(suggestion.id);
      return;
    }
    if (this.router.url.includes('/shop/category')) {
      this.navigationService.setPreviousUrl(this.router.url);
    }
    if (suggestion.type === 'category') {
      this.router.navigate(['/shop/category'], {
        queryParams: {
          search: null,
          categoryId: suggestion.id,
          subcategory: null,
          brands: null,
          productId: null,
          alternativeFor: null,
          sort: null,
          minPrice: null,
          maxPrice: null,
          adBannerId: null,
        },
        queryParamsHandling: 'merge',
      });
    }
    if (suggestion.type === 'subcategory') {
      this.router.navigate(['/shop/category'], {
        queryParams: {
          search: null,
          categoryId: suggestion.categoryId,
          subcategory: suggestion.name,
          brands: null,
          productId: null,
          sort: null,
          alternativeFor: null,
          minPrice: null,
          maxPrice: null,
          adBannerId: null,
        },
        queryParamsHandling: 'merge',
      });
    }
    if (suggestion.type === 'product') {
      this.router.navigate(['/shop/search'], {
        queryParams: {
          search: `${suggestion.brand.brandName} - ${suggestion.name}`,
          productId: suggestion.id,
          alternativeFor: null,
          categoryId: null,
          subcategory: null,
          brands: null,
          sort: null,
          minPrice: null,
          maxPrice: null,
          adBannerId: null,
        },
        queryParamsHandling: 'merge',
      });
    }
  }

  clearSearch(): void {
    if (this.router.url.includes('/shop/search')) {
      const previousUrl = this.navigationService.getPreviousUrl();
      if (previousUrl) {
        this.navigationService.setPreviousUrl(null);
        this.router.navigateByUrl(previousUrl);
      } else {
        this.router.navigate(['/shop'], {
          queryParams: {
            subcategory: null,
            brands: null,
            sort: null,
            productId: null,
            search: null,
            categoryId: null,
          },
          queryParamsHandling: 'merge',
        });
      }
    }
    this.searchControl.setValue('');
  }

  onFocus(): void {
    this.isFocused = true;
    this.previousValue = this.searchControl.value;
    this.searchControl.setValue('');
  }

  onBlur(): void {
    this.isFocused = false;
    if (!this.searchControl.value.trim()) {
      this.searchControl.setValue(this.previousValue);
    }
  }

  blockNonDigits(event: KeyboardEvent) {
    const input = this.searchControl.value || '';
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
    if (this.onlyIds && input.length >= 4) {
      event.preventDefault();
    }
  }

  removeIdTag(idTag: number) {
    this.productRemoved.emit(idTag);
  }

  sanitizeInput() {
    const value = this.searchControl.value || '';
    const numeric = value.replace(/\D+/g, '');
    if (value !== numeric) {
      this.searchControl.setValue(numeric, { emitEvent: false });
    }
  }
}
